import { Extension } from "@tiptap/core";
// Side-effect import: brings in `@tiptap/markdown`'s module augmentation
// that adds `markdown` to the `Editor` interface.
import "@tiptap/markdown";
import { Plugin, PluginKey } from "@tiptap/pm/state";

const MARKDOWN_PATTERNS = [
  /^#{1,6}\s/m, // headings
  /^```/m, // fenced code blocks
  /^[-*+]\s/m, // bulleted lists
  /^\d+\.\s/m, // numbered lists
  /^>\s/m, // blockquotes
  /^\|.*\|/m, // tables
  /\*\*[^*\n]+\*\*/, // bold **
  /__[^_\n]+__/, // bold __
  /\[[^\]\n]+\]\([^)\n]+\)/, // links
];

const looksLikeMarkdown = (text: string) => MARKDOWN_PATTERNS.some((pattern) => pattern.test(text));

const hasRealHTML = (html: string) => {
  // Consider a clipboard HTML payload "real" only if it contains structural
  // tags beyond the auto-wrapper some OSes add around plain text.
  const stripped = html
    .replace(/<\/?(html|head|body|meta|!doctype)[^>]*>/gi, "")
    .replace(/<!--[\s\S]*?-->/g, "")
    .trim();
  return /<[a-z][^>]*>/i.test(stripped) && !/^<p>[^<]*<\/p>$/i.test(stripped);
};

export const EditorExtensionMarkdownPaste = Extension.create({
  name: "markdownPaste",

  addProseMirrorPlugins() {
    const { editor } = this;

    return [
      new Plugin({
        key: new PluginKey("markdownPaste"),
        props: {
          handlePaste: (_view, event) => {
            const clipboard = event.clipboardData;
            if (!clipboard) {
              return false;
            }

            // Let FileHandler handle image/file pastes.
            if (clipboard.files && clipboard.files.length > 0) {
              return false;
            }

            // Let Tiptap's default HTML path run when the source provided real HTML.
            const html = clipboard.getData("text/html");
            if (html && hasRealHTML(html)) {
              return false;
            }

            const text = clipboard.getData("text/plain");
            if (!text || !looksLikeMarkdown(text)) {
              return false;
            }

            if (!editor.markdown) {
              return false;
            }

            const json = editor.markdown.parse(text);
            if (!json) {
              return false;
            }

            event.preventDefault();
            editor.commands.insertContent(json);
            return true;
          },
        },
      }),
    ];
  },
});
